/**
 * The DS menu panel + option row, as `sx`, for MUI popup surfaces.
 *
 * WHY THIS EXISTS AS `sx` AND NOT THE CANONICAL CLASS: canonical `.rw-menu` is written for the
 * `.rw-dropdown` wrapper case — a `position: relative` trigger with an absolutely-positioned panel —
 * so it carries `position: absolute; top: calc(100% + 4px); left: 0;` **and `width: 100%`**. A MUI
 * Menu / Select / Popover paper is portalled to `<body>` and positioned by Popover, so its containing
 * block is the viewport: `width: 100%` there means **100vw**, which is how a trailing-action ⋮ menu
 * can end up the width of the screen. The row class `.rw-menu-item` has no positioning in it and is
 * safe to use directly.
 *
 * Values below are canonical's own declarations for the same parts, bound to the same tokens. Lives
 * in its own module so consumers don't have to import `themeOptions`, which builds a theme at module
 * scope.
 *
 * Ported from `raptor-crm-fe` `src/common/presentation/styles/dsMenu.ts` (see `DS_VENDOR.md` for the
 * source SHA) and renamed to `dsMenuPaperSx.ts` per this repo's file-naming for the wrapper internals.
 */

/** Canonical `.rw-menu`, minus the positioning that a portalled paper must not inherit. */
export const DS_MENU_PAPER_SX = {
  backgroundColor: 'var(--surface-neutral-container-primary)',
  borderRadius: 'var(--border-radius-none)',
  border: 'none',
  // shadow-only, md tier — sm vanishes when a menu opens over a white surface.
  boxShadow: 'var(--shadow-md-offset-x) var(--shadow-md-offset-y) var(--shadow-md-blur) var(--shadow-md-spread) var(--shadow-md-color)',
  marginTop: 'var(--size-4)',
  // 320 matches canonical `.rw-menu { max-height: 320px }` verbatim. There is no --size-320 on the
  // scale (it stops at 256, then 360) — canonical hardcodes it too. Flagged, not invented.
  maxHeight: '320px',
  // A filter / long-option menu grows to its content and never truncates a label (rw-ds-components
  // §Picker). Cap = --size-256, matching the recorded decision on `.rw-filter-dropdown__panel`.
  width: 'max-content',
  maxWidth: 'var(--size-256)',
  // Canonical DS scroll track — a portalled paper can't inherit it from `.rw-menu`.
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-thumb': { background: 'var(--border-neutral-subtle)', borderRadius: '3px' }
} as const;

/**
 * Canonical `.rw-menu-item`, for MUI `MenuItem`s that can't carry the class themselves — e.g. a
 * `Select`, whose children are authored by each call site. A component that renders its own rows
 * should just use `className="rw-menu-item"` instead: the class is position-independent.
 */
export const DS_MENU_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--size-8)',
  minHeight: 'var(--size-40)',
  padding: '0 var(--size-16)',
  color: 'var(--text-body)',
  fontFamily: 'var(--font-label-large-family)',
  fontSize: 'var(--font-label-large-size)',
  fontWeight: 'var(--font-label-large-weight)',
  lineHeight: 'var(--font-label-large-line-height)',
  letterSpacing: 'var(--font-label-large-letter-spacing)',
  transition: 'background var(--duration-fast)',
  // Ellipsis is NOT set here: `text-overflow` doesn't apply to a flex container, so on this row it
  // would be inert and the label would hard-clip instead. It lives where canonical puts it, on
  // `.rw-menu-item__label`, which is already global.
  // Divider between rows, first row off.
  borderTop: 'var(--border-width-xs) solid var(--border-neutral-subtle)',
  '&:first-of-type': { borderTop: 'none' },
  '&:hover': { backgroundColor: 'var(--surface-neutral-subtle-hover)' },
  '&.Mui-focusVisible': {
    backgroundColor: 'var(--surface-neutral-subtle-hover)',
    // 2px inset ring, full-row — never orange. `outline: none` is required: the app-wide MuiMenuItem
    // override applies `focusVisibleSx`, whose outer 2px outline at +2px offset would otherwise
    // double the ring — and get clipped by the paper's overflow.
    outline: 'none',
    boxShadow: 'inset 0 0 0 var(--border-width-sm) var(--border-focus)'
  },
  // Selected = neutral, not brand.
  '&.Mui-selected': { backgroundColor: 'var(--surface-neutral-subtle-selected)' },
  '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
    backgroundColor: 'var(--surface-neutral-subtle-selected-hover)'
  },
  // MUI's MenuItem ships `opacity: 0.38` when disabled, which composites --text-disabled down to
  // low contrast. Opacity is never the disabled cue here — the token is.
  '&.Mui-disabled': { color: 'var(--text-disabled)', opacity: 1 },
  '&.Mui-disabled .MuiSvgIcon-root': { color: 'var(--icon-disabled)' },
  // Indentation ladder for hierarchical option rows (16/40/56/72). Consumers express depth with
  // `data-depth`; a hand-authored `pl` on the row loses to this block's specificity, so the ladder
  // is the only way depth survives.
  '&[data-depth="0"]': { paddingLeft: 'var(--size-16)' },
  '&[data-depth="1"]': { paddingLeft: 'var(--size-40)' },
  '&[data-depth="2"]': { paddingLeft: 'var(--size-56)' },
  '&[data-depth="3"]': { paddingLeft: 'var(--size-72)' }
  // No `__leading` / `__body` / `__label` / `__trailing` copies: canonical styles those classes
  // globally with no `.rw-menu` ancestor required. One home per fact — consumers compose the
  // canonical slots, canonical styles them.
} as const;
